import { cn } from '@/lib/utils';
import {
    ArrowDown,
    ArrowDownRight,
    ArrowRight,
    ArrowUp,
    ArrowUpRight,
    Ellipsis,
    Settings,
    Trash2
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { BriefKPI } from '@/pages/ScheduledAgents/data';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

export interface DisplayBrief {
    id: string;
    agentId: string;
    agentName: string;
    // Letter shown in the avatar circle — kept independent of `agentName`
    // since the live feed swaps run-title/config-name between agentName and
    // headline; the avatar should always represent the config, not the run.
    avatarLabel: string;
    agentColor: string;
    timeAgo: string;
    // Raw creation timestamp (ms since epoch) — `timeAgo` is a rounded
    // display string ("1d ago"), not precise enough to filter by. Used by
    // StreamAnalysis.tsx's timeline filter cutoff.
    createdAtMs: number;
    headline: string;
    summary: string;
    kpis: BriefKPI[];
    sources: string;
    // Whether the current user owns the underlying thread — only they may
    // delete it. My Feed only ever lists the viewer's own threads today, so
    // this is always true for live data, but kept explicit for when
    // cross-user visibility (subscribed jobs) lands.
    isOwner: boolean;
    // No real per-user "read" tracking exists yet (see the TODO in
    // StreamAnalysis.tsx's liveToDisplay) — this just drives the unread
    // dot + bold headline treatment below. Defaults to false so callers
    // that don't pass it get the plain/read look.
    isUnread?: boolean;
}

interface BriefCardProps {
    brief: DisplayBrief;
    // A real route, not a callback — BriefCard renders it as an <a> (via
    // react-router's Link) so the browser gives users native link behavior:
    // right-click "open in new tab", middle-click, ctrl/cmd-click. A JS
    // onClick handler can't offer any of that, which is what the previous
    // callback-based API silently broke.
    articleHref: string;
    onManage?: () => void;
    onDelete?: () => void;
}

export function BriefCard({
    brief,
    articleHref,
    onManage,
    onDelete
}: BriefCardProps) {
    return (
        <div
            data-brief-id={brief.id}
            className="group relative flex flex-col gap-2 bg-background shadow-xs hover:shadow-md p-8 border border-border dark:border-border rounded-md transition hover:-translate-y-1 transform-gpu scroll-mt-4 hover:cursor-pointer"
        >
            {/* Stretched-link overlay: makes the whole card (background,
                summary, KPI tiles, whitespace) act as the article link,
                including right-click "open in new tab". Sits above
                non-positioned siblings by default; the header/headline/
                footer links are lifted above it with `relative z-10` so
                they stay independently clickable/right-clickable rather
                than being shadowed by this overlay. Not a keyboard stop —
                the headline link below is the accessible entry point. */}
            <Link
                to={articleHref}
                tabIndex={-1}
                aria-hidden="true"
                className="z-0 absolute inset-0"
            />
            {/* Header row — `relative` so the unread badge centers against
                *this* row's actual height, not the whole card's. Being
                positioned it also sits above the stretched-link overlay;
                the avatar/name here aren't interactive, so that's only a
                minor loss of card-link coverage over the header. The "..."
                menu below is raised explicitly with z-10 as before. */}
            <div className="relative flex justify-between items-center mb-3">
                {/* Unread indicator, vertically centered against the header
                    row and right-aligned within it. Only fades out on
                    hover/while the actions dropdown is open when
                    onManage/onDelete exist — otherwise there's nothing to
                    make room for, so it stays put. Fades via opacity rather
                    than `hidden` (display:none can't transition, so that
                    snapped instead of animating) and drops pointer-events
                    while faded so it doesn't intercept clicks meant for the
                    ellipsis button underneath. */}
                {brief.isUnread && (
                    <Badge
                        variant="destructive"
                        className={cn(
                            'top-1/2 right-0 absolute bg-notification hover:bg-notification px-1.5 text-notification-foreground py-0.5 font-medium text-xs transition-opacity -translate-y-1/2 duration-200',
                            (onManage || onDelete) &&
                                'group-hover:opacity-0 group-has-data-[state=open]:opacity-0 group-hover:pointer-events-none group-has-data-[state=open]:pointer-events-none'
                        )}
                    >
                        New
                    </Badge>
                )}
                <div className="flex items-center gap-2.5">
                    <div
                        className="flex justify-center items-center rounded-md size-9 font-semibold text-white text-base"
                        style={{ backgroundColor: brief.agentColor }}
                    >
                        {brief.avatarLabel[0]}
                    </div>
                    <div>
                        <div className="font-medium text-base">
                            {brief.agentName}
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                            {brief.timeAgo}
                        </div>
                    </div>
                </div>
                {(onManage || onDelete) && (
                    <div
                        className="z-10 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="invisible group-has-data-[state=open]:visible group-hover:visible text-muted-foreground"
                                >
                                    <Ellipsis />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {onManage && (
                                    <DropdownMenuItem
                                        onClick={onManage}
                                       
                                    >
                                        <Settings />
                                        Manage job
                                    </DropdownMenuItem>
                                )}
                                {onManage && onDelete && (
                                    <DropdownMenuSeparator />
                                )}
                                {onDelete && (
                                    <DropdownMenuItem
                                        onClick={onDelete}
                                        variant="destructive"
                                       
                                    >
                                        <Trash2 />
                                        Delete
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
            </div>
            <Link
                to={articleHref}
                className="z-10 relative mb-2 font-medium text-foreground text-2xl text-left"
            >
                {brief.headline}
            </Link>

            {/* Summary */}
            <p className="mb-4 text-muted-foreground text-base">
                {brief.summary}
            </p>

            {/* KPI tiles */}
            <div className="gap-3 grid grid-cols-3 mb-4">
                {brief.kpis.map((kpi) => (
                    <KpiTile key={kpi.label} kpi={kpi} />
                ))}
            </div>

            {/* Footer */}
            <div className="z-10 relative flex justify-between items-center pt-3 border-border border-t">
                <span className="text-[13px] text-muted-foreground">
                    <span className="text-foreground">
                        {brief.sources}{' '}
                        {brief.sources === '1' ? 'source' : 'sources'}
                    </span>
                </span>
                <Button
                    asChild
                    className="group/link gap-1.5 text-sm"
                    variant="tertiary"
                    size="default"
                >
                    <Link to={articleHref}>
                        Open analysis
                        <span className="relative w-4 h-4 overflow-hidden">
                            <ArrowRight className="absolute inset-0 transition-transform group-hover/link:translate-x-full duration-300 ease-out" />
                            <ArrowRight className="absolute inset-0 transition-transform -translate-x-full group-hover/link:translate-x-0 duration-300 ease-out" />
                        </span>
                    </Link>
                </Button>
            </div>
        </div>
    );
}

function KpiTile({ kpi }: { kpi: BriefKPI }) {
    const deltaColorClass =
        kpi.delta === 'up'
            ? 'text-green-700 dark:text-green-500'
            : kpi.delta === 'down'
            ? 'text-red-700 dark:text-red-500'
            : 'text-muted-foreground';
    const ArrowIcon =
        kpi.delta === 'up'
            ? ArrowUpRight
            : kpi.delta === 'down'
            ? ArrowDownRight
            : ArrowRight;

    return (
        <div className="bg-background p-3 border border-border rounded-md">
            <div className="mb-1 text-muted-foreground text-xs truncate">
                {kpi.label}
            </div>
            <div
                className={cn(
                    'flex items-baseline font-medium text-2xl',
                    deltaColorClass
                )}
            >
                {kpi.value}
                <ArrowIcon className="ml-1 size-4" strokeWidth={3} />
            </div>
        </div>
    );
}
